import { showError, showSuccess } from "./toastUtils";

export const capitalize = (str) =>
  typeof str === "string" ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const timeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  for (const [unit, value] of Object.entries(intervals)) {
    const count = Math.floor(seconds / value);
    if (count >= 1) {
      return `${count} ${unit}${count > 1 ? "s" : ""} ago`;
    }
  }

  return "Just now";
};

export const getPrice = (p) => {
  if (!p) return 0;

  const type = p.type;
  const prop = p.property;

  switch (type) {
    case "land":
      return (
        prop?.expectedPrice ||
        prop?.pricePerSqft * (prop?.carpetArea?.value || 0)
      );

    case "commercial":
      return (
        prop?.allowedForConstructionDetails?.expectedPrice || prop?.price || 0
      );

    case "project":
      return (
        prop?.price ||
        (Array.isArray(prop?.floorPlan)
          ? prop.floorPlan?.[0]?.expectedPrice
          : 0)
      );

    case "residential":
      return prop?.floorPlan?.expectedPrice || prop?.price || 0;

    default:
      return 0;
  }
};

export const formatPrice = (price) => {
  if (isNaN(price)) return "Price on Request";
  if (price === 0) return 0;

  const num = Number(price);
  if (num === 0) return "₹0";

  const clean = (value) => {
    const fixed = value.toFixed(2);
    return fixed.endsWith(".00")
      ? fixed.slice(0, -3)
      : fixed.replace(/(\.\d)0$/, "$1");
  };

  // 1 Crore = 10,000,000
  if (num >= 1e7) {
    const cr = num / 1e7;
    return `₹${clean(cr)} Cr`;
  }

  // 1 Lakh = 100,000
  if (num >= 1e5) {
    const lakh = num / 1e5;
    return `₹${clean(lakh)} Lakh`;
  }

  // 1 Thousand = 1,000
  if (num >= 1e3) {
    const thousand = num / 1e3;
    return `₹${clean(thousand)} K`;
  }

  // Below 1K – just show number with commas
  return `₹${num.toLocaleString("en-IN")}`;
};

export const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const formatFloor = (floor) => {
  if (floor === 0) return "Ground Floor";
  if (floor === 1) return "1st Floor";
  if (floor === 2) return "2nd Floor";
  if (floor === 3) return "3rd Floor";
  return `${floor}th Floor`;
};

const downloadCooldownMap = new Map();

export const downloadFile = async (fileUrl, filename = "file.pdf") => {
  if (!fileUrl) {
    showError("No file available to download");
    return;
  }

  //  If same file was requested again too fast → block
  if (downloadCooldownMap.has(fileUrl)) {
    showError("Please wait before downloading again...");
    return;
  }

  //  Set lock for this specific file
  downloadCooldownMap.set(fileUrl, true);

  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      showError("Failed to fetch file");
      downloadCooldownMap.delete(fileUrl);
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();

    showSuccess("Downloaded successfully");
  } catch (error) {
    console.error(error);
    showError("Download failed");
  } finally {
    // Release lock after cooldown (2–3 seconds recommended)
    setTimeout(() => {
      downloadCooldownMap.delete(fileUrl);
    }, 2500);
  }
};

export const formatPrettyAddress = (location) => {
  try {
    if (!location) return "Address unavailable";

    const addr = location.address;

    // Old format (plain string)
    if (typeof addr === "string") {
      return addr.trim() || "Address not provided";
    }

    // New structured format
    if (addr && typeof addr === "object") {
      const { locality, city, formatted } = addr;

      if (locality && city) return `${locality}, ${city}`;
      if (city) return city;
      if (locality) return locality;
      if (formatted) return formatted;

      return "Address not available";
    }

    // Unknown structure
    return "Address not available";
  } catch (error) {
    console.error("Error formatting address:", error);
    return "Address unavailable";
  }
};
export const openInNewTab = (url) => {
  if (!url) return;

  window.open(url, "_blank", "noopener,noreferrer");
};

export const cleanArray = (arr) =>
  arr
    ?.map((item) => item?.trim()) // remove extra spaces
    .filter((item) => item && item.length) // remove empty strings
    .filter((item, index, self) => self.indexOf(item) === index); // unique

export const getPublicPropertyRoute = (property) => {
  if (!property?._id || !property?.type) return "/";

  const id = property._id;
  const type = property.type.toLowerCase();

  switch (type) {
    case "residential":
      return `/properties/residential/${id}`;
    case "commercial":
      return `/properties/commercial/${id}`;
    case "land":
      return `/properties/land/${id}`;
    case "project":
      return `/properties/project/${id}`;
    default:
      return "/";
  }
};
